import { ReactNode, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    ChevronLeft,
    ChevronRight,
    Users,
    BookOpen,
    Settings as SettingsIcon,
    Shield,
    Bell,
    Database,
    UserIcon,
    Link as LinkIcon,
    Key,
    Puzzle
} from "lucide-react";
import { User } from "../types/features/User";

interface SettingsLayoutProps {
    children: ReactNode;
    role?: User["role"]
}

export default function SettingsLayout({ children, role = "entreprise" }: SettingsLayoutProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const basePath = `/${role}/settings`;

    const tabItems = [
        {
            label: "Compte et facturation",
            path: "/account",
            icon: UserIcon,
            externe: false
        },
        {
            label: "Préférences",
            path: "/preferences",
            icon: Bell,
            externe: false

        },
        {
            label: "Modules",
            path: "/entreprise-module",
            icon: Puzzle,
            externe: false

        },
        {
            label: "Base de données",
            path: "/database",
            icon: Database,
            externe: false

        },
        {
            label: "Intégrations API",
            path: "/entreprise/integrations",
            icon: LinkIcon,
            externe: true

        },
    ];

    // Filtrer les tabs selon le rôle
    const getVisibleTabs = () => {
        // Si c'est un admin (super admin), il voit tout
        if (role === 'admin') {
            return tabItems;
        }

        // Si c'est une entreprise, elle voit tout sauf "Sécurité" (qui est pour le super admin)
        if (role === 'entreprise') {
            return tabItems.filter(item => item.path !== '/security');
        }

        // Si c'est un agent, il ne voit rien (pas d'accès à la settings)
        return [];
    };

    const visibleTabs = getVisibleTabs();

    // Fonction pour vérifier si un tab est actif
    const isActive = (path: string) => {
        const currentPath = location.pathname;

        if (path === "") {
            return currentPath === basePath;
        } else {
            return currentPath.includes(path);
        }
    };

    // Scroll vers la gauche
    const scrollLeft = () => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
        }
    };

    // Scroll vers la droite
    const scrollRight = () => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
        }
    };

    // Scroll automatiquement vers le tab actif au chargement
    useEffect(() => {
        const activeIndex = visibleTabs.findIndex(item => isActive(item.path));
        if (activeIndex !== -1 && scrollContainerRef.current) {
            const container = scrollContainerRef.current;
            const activeTab = container.children[activeIndex] as HTMLElement;

            if (activeTab) {
                const containerWidth = container.offsetWidth;
                const tabLeft = activeTab.offsetLeft;
                const tabWidth = activeTab.offsetWidth;

                const scrollPosition = tabLeft - (containerWidth / 2) + (tabWidth / 2);
                container.scrollTo({ left: scrollPosition, behavior: 'smooth' });
            }
        }
    }, [location.pathname, visibleTabs]);

    // Si l'agent n'a pas accès à la settings
    if (visibleTabs.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-64 text-center">
                <p className="text-gray-500">Vous n'avez pas accès à cette page.</p>
                <button
                    onClick={() => navigate(`/${role}/dashboard`)}
                    className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
                >
                    Retour au tableau de bord
                </button>
            </div>
        );
    }

    return (
        <div className="">
            {/* Barre de navigation avec tabs scrollables */}
            <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
                <div className="relative flex items-center">
                    {/* Bouton scroll gauche */}
                    <button
                        onClick={scrollLeft}
                        className="absolute left-0 z-10 bg-white h-full px-2 shadow-right hover:text-primary transition-colors hidden md:flex items-center"
                        style={{ boxShadow: '4px 0 8px -4px rgba(0,0,0,0.1)' }}
                    >
                        <ChevronLeft size={20} className="text-gray-500" />
                    </button>

                    {/* Conteneur scrollable des tabs */}
                    <div
                        ref={scrollContainerRef}
                        className="flex-1 overflow-x-auto scrollbar-hide px-8 md:px-10"
                        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                    >
                        <div className="flex space-x-1 min-w-max py-2">
                            {visibleTabs.map((item) => {
                                const Icon = item.icon;
                                const active = isActive(item.path);
                                return (
                                    <button
                                        key={item.path}
                                        onClick={() => {
                                            if (item.externe) {
                                                navigate(`${item.path}`)
                                            } else {
                                                navigate(`${basePath}${item.path}`)
                                            }
                                        }}
                                        className={`
                                            flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap
                                            ${active
                                                ? 'bg-primary text-white shadow-md'
                                                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                                            }
                                        `}
                                    >
                                        <Icon className="w-4 h-4" />
                                        {item.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Bouton scroll droit */}
                    <button
                        onClick={scrollRight}
                        className="absolute right-0 z-10 bg-white h-full px-2 shadow-left hover:text-primary transition-colors hidden md:flex items-center"
                        style={{ boxShadow: '-4px 0 8px -4px rgba(0,0,0,0.1)' }}
                    >
                        <ChevronRight size={20} className="text-gray-500" />
                    </button>
                </div>
            </div>

            {/* Contenu de la page */}
            <div className="px-6 py-10">
                {children}
            </div>

            {/* Style pour cacher la scrollbar tout en gardant la fonctionnalité */}
            <style>{`
                .scrollbar-hide::-webkit-scrollbar {
                    display: none;
                }
            `}</style>
        </div>
    );
}